// AccordionFAQMobileComponent.js
import React, { useState } from 'react';
import { Box, Typography, IconButton, Collapse } from '@mui/material';
import ScrollFadeWrapper from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const AccordionFAQMobileComponent = ({ data, defaultOpenIndex = null }) => {
  const [openIndex, setOpenIndex] = useState(defaultOpenIndex);

  const toggle = (index) => setOpenIndex(index === openIndex ? null : index);

  const CELL_TEXT_SX = {
    color: '#162F3B',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    letterSpacing: '-0.002em',
    m: 0,
    width: '100%', // so textAlign works
    textAlign: 'left',
  };

  const IconCell = ({ icon }) => (
    <Typography
      component="div"
      role="img"
      aria-label={icon === 'check' ? 'Yes' : 'No'}
      sx={CELL_TEXT_SX}
    >
      {icon === 'check' ? '✓' : '✕'}
    </Typography>
  );

  const PlanTable = ({ table }) => {
    const rows = Array.isArray(table?.rows) ? table.rows : [];

    return (
      <Box
        sx={{
          mt: '16px',
          borderRadius: '24px',
          overflow: 'hidden',
          border: '1px solid #E3E6E8',
          bgcolor: '#FFF',
        }}
      >
        {rows.map((r, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              borderBottom:
                i === rows.length - 1 ? 'none' : '1px solid #E3E6E8',
              px: '16px',
              py: '12px',
              gap: '6px',
            }}
          >
            <Typography
              sx={{
                color: '#162F3B',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '0.8125rem',
                lineHeight: '1rem',
                letterSpacing: '-0.01em',
                m: 0,
              }}
            >
              {r.label}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
              }}
            >
              {typeof r.value === 'string' ? (
                <Typography sx={CELL_TEXT_SX}>{r.value}</Typography>
              ) : (
                <IconCell icon={r.value.icon} />
              )}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100vw',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '32px',
      }}
    >
      {/* Title */}
      <ScrollFadeWrapper variant="text">
        <Box sx={{ textAlign: 'center' }}>
          {data.title.split('\n').map((line, i) => (
            <Typography
              component="h2"
              key={i}
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '2rem',
                lineHeight: '2.25rem',
                letterSpacing: '-0.03em',
                color: '#162F3B',
              }}
            >
              {line}
            </Typography>
          ))}
        </Box>
      </ScrollFadeWrapper>

      {/* FAQ Blue Container */}
      <Box
        sx={{
          width: '100%',
          backgroundColor: '#162F3B',
          borderRadius: '34px',
          padding: '32px 16px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
      >
        {data.introText && (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              mb: '10px',
            }}
          >
            <Box
              sx={{
                width: '1010px',
                backgroundColor: '#162F3B',
                borderRadius: '24px',
                boxSizing: 'border-box',
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{
                  color: '#FFFFFF',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontStyle: 'normal',
                  fontSize: '1.5rem',
                  lineHeight: '1.75rem',
                  letterSpacing: '-0.03em',
                  textAlign: 'center',
                  whiteSpace: 'pre-line',
                }}
              >
                {data.introText}
              </Typography>
            </Box>
          </Box>
        )}

        {data.faq.map((faq, i) => {
          const isOpen = openIndex === i;

          return (
            <ScrollFadeWrapper key={i}>
              <Box
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                onClick={() => toggle(i)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggle(i);
                  }
                }}
                sx={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '24px',
                  p: '16px',
                  cursor: 'pointer',
                  boxShadow:
                    '0px 3px 8px 0px #162F3B1F, 0px 1px 2px 0px #A2ACB133',
                  display: 'flex',
                  flexDirection: 'column',
                  boxSizing: 'border-box',
                  transition: 'box-shadow 0.2s ease',
                  outline: 'none',
                  '&:focus-visible': { boxShadow: '0 0 0 3px #A2ACB166' },
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '1.25rem',
                      lineHeight: '1.5rem',
                      letterSpacing: '-0.03em',
                      color: '#162F3B',
                    }}
                  >
                    {faq.title}
                  </Typography>

                  <IconButton
                    disableRipple
                    tabIndex={-1}
                    sx={{
                      width: '32px',
                      height: '32px',
                      color: '#162F3B',
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 200ms ease',
                    }}
                    aria-label={isOpen ? 'Collapse answer' : 'Expand answer'}
                  >
                    <ArrowDropDownIcon sx={{ fontSize: '32px' }} />
                  </IconButton>
                </Box>

                {/* Animated content */}
                {/* Animated content */}
                <Collapse in={isOpen} timeout={250}>
                  {faq.table ? (
                    <PlanTable table={faq.table} />
                  ) : (
                    <Typography
                      sx={{
                        mt: '16px',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: '1rem',
                        lineHeight: '1.5rem',
                        letterSpacing: '-0.02em',
                        color: '#162F3B',
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {faq.description}
                    </Typography>
                  )}
                </Collapse>
              </Box>
            </ScrollFadeWrapper>
          );
        })}
      </Box>
    </Box>
  );
};

export default AccordionFAQMobileComponent;
